import React, { useRef } from 'react';
import {
    VideoCameraOutlineSmall,
    FloorplanSmall,
    BedMedium,
    BathMedium,
    SofaMedium,
    AreaMedium,
    CalendarMedium
} from '@blueprint/icons';
import { buildCombinedVirtualTours } from 'listing-details/helpers';
import { ButtonText } from 'listing-details/components/Buttons/Buttons';
import {
    LISTING_SUMMARY_CONSTANTS,
    getPricingDetailLabel,
    getAttributes
} from './helpers';

import {
    ListingSummaryDetailsProps,
    SECTIONS,
    ProcessedAttributesReturnType
} from 'listing-details/components/ListingSummaryDetails/ListingSummaryDetails.types';

import {
    trackOpenVirtualTours,
    trackOpenFloorPlans,
    trackOpenTenancyInfo,
    trackOpenGuidePrice
} from 'listing-details/components/ListingSummaryDetails/ListingSummaryDetails.tracking';

import {
    SummaryContainer,
    HeaderContainer,
    ToursFloorplanLaunchers,
    LauncherButton,
    DividerVertical,
    DetailsContainer,
    PricingLabel,
    PricingDetail,
    AdditionalInfo,
    BodyContainer,
    TitleWrapper,
    DisplayTitleLabel,
    DisplayAddressLabel,
    Attributes,
    Attribute,
    AttributeLabel,
    AvailableFrom,
    PricingQualifier
} from './ListingSummaryDetails.styles';
import { format, isAfter, parseISO } from 'date-fns';
import { ListingTags } from '../shared';
import { hasPriceQualifierLabel } from 'listing-details/helpers';

const ListingSummaryDetails = ({
    data,
    openModal
}: ListingSummaryDetailsProps) => {
    const floorPlanModalTriggerOrigin = useRef(null);
    const virtualTourModalTriggerOrigin = useRef(null);
    const tenancyInfoModalTriggerOrigin = useRef(null);
    const guidePriceModalTriggerOrigin = useRef(null);

    const virtualToursClick = () => {
        trackOpenVirtualTours();
        openModal('tours', virtualTourModalTriggerOrigin);
    };

    const floorPlansClick = () => {
        trackOpenFloorPlans();
        openModal('floorplans', floorPlanModalTriggerOrigin);
    };

    const tenancyInfoClick = () => {
        trackOpenTenancyInfo();
        openModal('tenancy_info', tenancyInfoModalTriggerOrigin);
    };

    const guidePriceClick = () => {
        trackOpenGuidePrice();
        openModal('guide_price', guidePriceModalTriggerOrigin);
    };

    const isToRent = data.analyticsTaxonomy.section === SECTIONS.TO_RENT;
    const pricingDetailLabel = getPricingDetailLabel(data);
    const attributes: ProcessedAttributesReturnType = getAttributes(data);

    const { pricing } = data;

    const { tours, videos, links } = data?.embeddedContent;
    const hasVirtualTours = Boolean(
        buildCombinedVirtualTours(tours || [], videos || [], links || []).length
    );

    const hasFloorPlans = Boolean(data?.content?.floorPlan?.length);
    const flags = data?.features?.flags;
    const availableDate =
        isToRent && flags?.availableFromDate
            ? parseISO(flags?.availableFromDate)
            : null;

    return (
        <SummaryContainer
            aria-labelledby="listing-summary-details-heading"
            data-testid="listing-summary-details"
        >
            <HeaderContainer>
                {data?.content && (
                    <ToursFloorplanLaunchers data-testid="virtual-tours-floor-plan-wrapper">
                        {hasVirtualTours && (
                            <LauncherButton
                                variant="tertiary"
                                ref={virtualTourModalTriggerOrigin}
                                onClick={virtualToursClick}
                                data-testid="virtual-tours-label"
                            >
                                <VideoCameraOutlineSmall data-testid="virtual-tours-icon" />
                                {LISTING_SUMMARY_CONSTANTS.virtualTours}
                            </LauncherButton>
                        )}

                        {hasVirtualTours && hasFloorPlans && (
                            <DividerVertical variant="vertical" aria-hidden />
                        )}

                        {hasFloorPlans && (
                            <LauncherButton
                                variant="tertiary"
                                ref={floorPlanModalTriggerOrigin}
                                onClick={floorPlansClick}
                                data-testid="floorplans-label"
                            >
                                <FloorplanSmall data-testid="floorplans-icon" />
                                {LISTING_SUMMARY_CONSTANTS.floorplans}
                            </LauncherButton>
                        )}
                    </ToursFloorplanLaunchers>
                )}

                <DetailsContainer>
                    <ListingTags data={data} />

                    {hasPriceQualifierLabel(pricing.priceQualifierLabel) ? (
                        <PricingQualifier data-testid="price-qualifier">
                            {pricing.priceQualifierLabel}
                        </PricingQualifier>
                    ) : null}

                    {pricing.label ? (
                        <PricingLabel data-testid="price">
                            {pricing.label}
                        </PricingLabel>
                    ) : null}

                    {pricingDetailLabel && (
                        <PricingDetail
                            size="small"
                            data-testid="rentalfrequency-and-floorareaunit"
                        >
                            {pricingDetailLabel}
                        </PricingDetail>
                    )}

                    {Boolean(isToRent && data.administrationFees) && (
                        <AdditionalInfo data-testid="tenancy-information">
                            <ButtonText
                                onClick={tenancyInfoClick}
                                ref={tenancyInfoModalTriggerOrigin}
                            >
                                {
                                    LISTING_SUMMARY_CONSTANTS.tenancyInformationText
                                }
                            </ButtonText>
                        </AdditionalInfo>
                    )}

                    {pricing.priceQualifierLabel === 'Guide price' &&
                        pricing.isAuction && (
                            <AdditionalInfo data-testid="guide-price">
                                <ButtonText
                                    onClick={guidePriceClick}
                                    ref={guidePriceModalTriggerOrigin}
                                >
                                    What is a guide price?
                                </ButtonText>
                            </AdditionalInfo>
                        )}
                </DetailsContainer>
            </HeaderContainer>
            <BodyContainer>
                <TitleWrapper id="listing-summary-details-heading">
                    <DisplayTitleLabel data-testid="title-label">
                        {data.title}{' '}
                    </DisplayTitleLabel>
                    <DisplayAddressLabel data-testid="address-label">
                        {data.displayAddress}
                    </DisplayAddressLabel>
                </TitleWrapper>

                {attributes && (
                    <Attributes>
                        {attributes?.numBedrooms && (
                            <Attribute>
                                <BedMedium data-testid="beds-icon" />
                                <AttributeLabel data-testid="beds-label">
                                    {attributes.numBedrooms}
                                </AttributeLabel>
                            </Attribute>
                        )}
                        {attributes?.numBathrooms && (
                            <Attribute>
                                <BathMedium data-testid="baths-icon" />
                                <AttributeLabel data-testid="baths-label">
                                    {attributes.numBathrooms}
                                </AttributeLabel>
                            </Attribute>
                        )}
                        {attributes?.numLivingRooms && (
                            <Attribute>
                                <SofaMedium data-testid="receptions-icon" />
                                <AttributeLabel data-testid="receptions-label">
                                    {attributes.numLivingRooms}
                                </AttributeLabel>
                            </Attribute>
                        )}
                        {attributes.floorAreaLabel && (
                            <Attribute>
                                <AreaMedium data-testid="floorarea-icon" />
                                <AttributeLabel data-testid="floorarea-label">
                                    {attributes.floorAreaLabel}
                                </AttributeLabel>
                            </Attribute>
                        )}
                    </Attributes>
                )}
            </BodyContainer>
            {availableDate ? (
                <AvailableFrom data-testid="availability">
                    <CalendarMedium />
                    {isAfter(availableDate, new Date())
                        ? `Available from ${format(
                              availableDate,
                              'dd MMMM yyyy'
                          )}`
                        : 'Available immediately'}
                </AvailableFrom>
            ) : null}
        </SummaryContainer>
    );
};

export default ListingSummaryDetails;
